/** Interpreta datas vindas da API (ISO, BR ou texto já exibível). */
export function parseApiDateTime(
  raw: string | null | undefined,
): Date | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || s === "-") return null;
  if (/^0001-01-01/.test(s)) return null;

  const brDateTime = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s*[-–]\s*|\s+)(\d{1,2}):(\d{2})/,
  );
  if (brDateTime) {
    const d = new Date(
      Number(brDateTime[3]),
      Number(brDateTime[2]) - 1,
      Number(brDateTime[1]),
      Number(brDateTime[4]),
      Number(brDateTime[5]),
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const brDate = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (brDate) {
    const d = new Date(
      Number(brDate[3]),
      Number(brDate[2]) - 1,
      Number(brDate[1]),
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const isoPrefix = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoPrefix) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Formato somente data: DD/MM/AAAA (retorna "-" se inválido). */
export function formatDateBR(dateString: string | null | undefined): string {
  if (dateString != null) {
    const s = String(dateString).trim();
    // Datas ISO (com ou sem horário/timezone) representam um dia de
    // calendário: lemos os dígitos Y-M-D direto da string para não sofrer
    // deslocamento de fuso horário ao converter para Date local.
    const isoPrefix = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoPrefix && !/^0001-01-01/.test(s)) {
      return `${isoPrefix[3]}/${isoPrefix[2]}/${isoPrefix[1]}`;
    }
  }

  const date = parseApiDateTime(dateString);
  if (!date) return "-";

  const dia = String(date.getDate()).padStart(2, "0");
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const ano = date.getFullYear();
  return `${dia}/${mes}/${ano}`;
}

export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "-";

  const date = parseApiDateTime(dateString);
  if (!date) return "-";

  return `${date.toLocaleDateString()} - ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

/** Formato da listagem de extratos: DD/MM/AAAA às HH:mmh */
export function formatDateTimeExtratoList(
  dateString: string | null | undefined,
): string {
  if (!dateString) return "-";

  const date = parseApiDateTime(dateString);
  if (!date) return "-";

  const dia = String(date.getDate()).padStart(2, "0");
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const ano = date.getFullYear();
  const hora = String(date.getHours()).padStart(2, "0");
  const minutos = String(date.getMinutes()).padStart(2, "0");

  return `${dia}/${mes}/${ano} às ${hora}:${minutos}h`;
}

export function formatDateTimeBR(dateString: string | null | undefined): string {
  if (!dateString) return "-";

  const date = parseApiDateTime(dateString);
  if (!date) return "-";

  const dia = String(date.getDate()).padStart(2, "0");
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const ano = date.getFullYear();
  const hora = String(date.getHours()).padStart(2, "0");
  const minutos = String(date.getMinutes()).padStart(2, "0");

  return `${dia}/${mes}/${ano} - ${hora}:${minutos}`;
}

/** Mesmo padrão visual do histórico V2 (DD/MM/AAAA - HH:mm), inclusive para legado já retornado assim pela API. */
export function formatHistoricoDocumentacaoDataHora(
  raw: string | null | undefined,
): string {
  if (!raw) return "-";
  const s = String(raw).trim();
  if (s === "-") return "-";
  const jaExibicaoBr =
    /^\d{1,2}\/\d{1,2}\/\d{4}\s*-\s*\d{1,2}:\d{1,2}(?::\d{1,2})?$/;
  if (jaExibicaoBr.test(s)) return s;
  return formatDateTimeBR(s);
}


export function parseHistoricoDataHoraDisplay(
  s: string | null | undefined,
): number {
  if (!s || s === "-") return 0;
  const trimmed = String(s).trim();
  const br = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s*-\s*(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/,
  );
  if (br) {
    const d = Number(br[1]);
    const mo = Number(br[2]);
    const y = Number(br[3]);
    const h = Number(br[4]);
    const mi = Number(br[5]);
    const sc = br[6] != null ? Number(br[6]) : 0;
    const t = new Date(y, mo - 1, d, h, mi, sc).getTime();
    return Number.isFinite(t) ? t : 0;
  }
  const tIso = new Date(trimmed).getTime();
  return Number.isFinite(tIso) && !Number.isNaN(tIso) ? tIso : 0;
}

export type HistoricoDocumentacaoEntry = {
  acao: string;
  data: string;
  
  id?: number | string | null;
};

const historicoIdParaOrdenar = (x: HistoricoDocumentacaoEntry): number | null => {
  const raw = x.id;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};


export function sortHistoricoDocumentacaoDesc<T extends HistoricoDocumentacaoEntry>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    const idA = historicoIdParaOrdenar(a);
    const idB = historicoIdParaOrdenar(b);
    if (idA != null && idB != null) {
      return idB - idA;
    }
    return (
      parseHistoricoDataHoraDisplay(b.data) - parseHistoricoDataHoraDisplay(a.data)
    );
  });
}

